import IconTick from 'assets/icons/icon-tick.svg';
import { hideVisually, rem } from 'polished';
import styled, { css } from 'styled-components';

const CHECKBOX_HEIGHT = 22;

interface WrapperProps {
  slim?: boolean;
}

export const CheckboxWrapper = styled.span<WrapperProps>`
  display: flex;

  /* Overrides for "slim" variant */
  ${({ theme, slim }) => {
    if (slim) {
      return css`
        min-height: 0;
        margin-top: 20px;
      `;
    }
  }}
`;

export const Input = styled.input`
  ${hideVisually()}
`;

interface LabelProps {
  align?: 'center' | 'top';
  slim?: boolean;
  bold?: boolean;
}

export const Label = styled.label<LabelProps>`
  position: relative;
  display: inline-flex;
  align-items: ${({ align }) => (align === 'top' ? 'flex-start' : 'center')};
  width: 100%;
  min-height: ${({ align }) => (align === 'top' ? 'auto' : rem(80))};
  margin: ${rem(5)} 0; /* Needed for box-shadow to work */
  padding: ${rem(12)} ${rem(20)} ${rem(12)} ${rem(54)};
  color: ${({ theme }) => theme.colors.palette.black};
  font-weight: ${({ bold }) => (bold ? 700 : 400)};
  font-size: ${rem(16)};
  line-height: ${rem(CHECKBOX_HEIGHT)};
  background-color: ${({ theme }) => theme.colors.palette.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};

  ${({ theme }) => theme.mediaQueries.medium} {
    min-height: ${({ align, slim }) =>
      slim ? 0 : align === 'top' ? 'auto' : rem(70)};
  }

  svg {
    margin-right: ${rem(12)};
    margin-left: ${rem(4)};
  }

  &::before,
  &::after {
    position: absolute;
    top: ${({ align }) => (align === 'top' ? rem(22) : '50%')};
    left: ${rem(20)};
    display: inline-block;
    transform: translateY(-50%);
    content: '';
  }

  &::before {
    width: ${rem(CHECKBOX_HEIGHT)};
    height: ${rem(CHECKBOX_HEIGHT)};
    background: white;
    border: 2px solid ${({ theme }) => theme.colors.palette.black};
  }

  &::after {
    width: ${rem(CHECKBOX_HEIGHT)};
    height: ${rem(CHECKBOX_HEIGHT)};
  }

  &:hover {
    &::before {
      border-color: ${({ theme }) => theme.colors.palette.teal};
    }
  }

  ${Input}:focus ~ & {
    box-shadow:
      0 0 0 2px ${({ theme }) => theme.colors.palette.redDark},
      0 0 8px ${({ theme }) => theme.colors.palette.redDark};
  }

  ${Input}:checked ~ &::before {
    background-color: ${({ theme }) => theme.colors.palette.green};
    border-color: ${({ theme }) => theme.colors.palette.green};
  }

  ${Input}:disabled ~ &::before {
    border-color: ${({ theme }) => theme.colors.palette.greyLight};
  }

  ${Input}:disabled:checked ~ &::before {
    background-color: ${({ theme }) => theme.colors.palette.greyLight};
  }

  ${Input}[type='checkbox']:checked ~ &::after {
    background-color: white;
    mask-image: url('${IconTick}');
    mask-repeat: no-repeat;
    mask-size: 15px 10px;
    mask-position: 60% 55%; /* specific alignment instead of 'center' */
  }

  ${Input}[type='checkbox'] ~ &::before {
    border-radius: ${({ theme }) => theme.borderRadius.small};
  }

  ${Input}[type='radio'] ~ &::before {
    border-radius: 50%;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.palette.white};
  }

  /* Overrides for "slim" variant */
  ${({ theme, slim }) => {
    if (slim) {
      return css`
        min-height: 0;
        padding: 0;
        padding-left: ${rem(35)};
        text-align: left;
        background: none;

        /* stylelint-disable */
        &::before,
        &::after {
          left: 0;
        }
      `;
    }
  }}
`;
